import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { generateICS } from '../utils/icsGenerator';

export default function CalendarIntegration({ meetingId, onClose }) {
  const { user, userProfile, requestCalendarAccess } = useAuth();
  const toast = useToast();

  const [eventDetails, setEventDetails] = useState({
    title: `QuickMeet: ${meetingId}`,
    startTime: new Date(),
    endTime: new Date(Date.now() + 30 * 60 * 1000), // 30 minutes from now
    description: ''
  });

  const [isAddingToCalendar, setIsAddingToCalendar] = useState(false);

  const handleGoogleCalendar = async () => {
    setIsAddingToCalendar(true);
    try {
      // Check if we have a valid token
      let accessToken = userProfile?.googleCalendarToken;
      const tokenExpiry = userProfile?.calendarTokenExpiry?.toDate?.();
      const isTokenExpired = tokenExpiry ? new Date() > tokenExpiry : true;

      // Request access if no token or expired
      if (!accessToken || isTokenExpired) {
        try {
          accessToken = await requestCalendarAccess();
        } catch (error) {
          // User cancelled or error occurred
          throw new Error('Calendar access denied. Please try again.');
        }
      }

      // Create calendar event
      const meetingUrl = `${window.location.origin}/meet/${meetingId}`;
      const event = {
        summary: eventDetails.title,
        description: eventDetails.description
          ? `${eventDetails.description}\\n\\nJoin QuickMeet: ${meetingUrl}`
          : `Join QuickMeet: ${meetingUrl}`,
        start: {
          dateTime: eventDetails.startTime.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        end: {
          dateTime: eventDetails.endTime.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        conferenceData: {
          entryPoints: [{
            entryPointType: 'video',
            uri: meetingUrl
          }]
        }
      };

      const response = await fetch(
        'https://www.googleapis.com/calendar/v3/calendars/primary/events?conferenceDataVersion=1',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(event)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to create calendar event');
      }

      const createdEvent = await response.json();
      toast.success('Meeting added to Google Calendar!');
      onClose();
    } catch (error) {
      console.error('Error adding to Google Calendar:', error);
      toast.error(error.message || 'Failed to add to calendar. Please try again.');
    } finally {
      setIsAddingToCalendar(false);
    }
  };

  const handleDownloadICS = () => {
    try {
      const meetingUrl = `${window.location.origin}/meet/${meetingId}`;
      generateICS(meetingId, {
        ...eventDetails,
        description: eventDetails.description || `Join QuickMeet: ${meetingUrl}`
      });
      toast.success('.ics file downloaded!');
      onClose();
    } catch (error) {
      console.error('Error generating .ics file:', error);
      toast.error('Failed to generate calendar file');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl max-w-lg w-full p-8 animate-fade-in">
        <h3 className="text-2xl font-semibold text-[#1d1d1f] dark:text-white mb-2">
          Add to Calendar
        </h3>
        <p className="text-[#6e6e73] dark:text-[#8e8e93] mb-6">
          Schedule this meeting in your calendar
        </p>

        {/* Event Details Form */}
        <div className="space-y-4 mb-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
              Meeting Title
            </label>
            <input
              type="text"
              value={eventDetails.title}
              onChange={(e) => setEventDetails({ ...eventDetails, title: e.target.value })}
              className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all"
            />
          </div>

          {/* Start Time */}
          <div>
            <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
              Start Time
            </label>
            <input
              type="datetime-local"
              value={eventDetails.startTime.toISOString().slice(0, 16)}
              onChange={(e) => {
                const newStartTime = new Date(e.target.value);
                setEventDetails({
                  ...eventDetails,
                  startTime: newStartTime,
                  endTime: new Date(newStartTime.getTime() + 30 * 60 * 1000)
                });
              }}
              className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all"
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
              Duration
            </label>
            <select
              value={Math.floor((eventDetails.endTime - eventDetails.startTime) / (60 * 1000))}
              onChange={(e) => {
                const durationMinutes = parseInt(e.target.value);
                setEventDetails({
                  ...eventDetails,
                  endTime: new Date(eventDetails.startTime.getTime() + durationMinutes * 60 * 1000)
                });
              }}
              className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all"
            >
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="45">45 minutes</option>
              <option value="60">1 hour</option>
              <option value="90">1.5 hours</option>
              <option value="120">2 hours</option>
            </select>
          </div>

          {/* Description (Optional) */}
          <div>
            <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
              Description (Optional)
            </label>
            <textarea
              value={eventDetails.description}
              onChange={(e) => setEventDetails({ ...eventDetails, description: e.target.value })}
              placeholder="Add meeting notes or agenda..."
              rows="3"
              className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93] focus:outline-none focus:ring-2 focus:ring-[#06c] transition-all resize-none"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleGoogleCalendar}
            disabled={isAddingToCalendar}
            className="w-full px-4 py-3 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-xl font-medium hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
          >
            {isAddingToCalendar ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Adding to Calendar...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.545 12.151L12.545 6.545 11.455 6.545 11.455 12.151 5.849 12.151 5.849 13.241 11.455 13.241 11.455 18.848 12.545 18.848 12.545 13.241 18.151 13.241 18.151 12.151z"/>
                </svg>
                <span>Add to Google Calendar</span>
              </>
            )}
          </button>

          <button
            onClick={handleDownloadICS}
            className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-xl font-medium hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>Download .ics File</span>
          </button>

          <button
            onClick={onClose}
            className="w-full px-4 py-3 text-[#6e6e73] dark:text-[#8e8e93] rounded-xl font-medium hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] transition-all"
          >
            Skip for Now
          </button>
        </div>
      </div>
    </div>
  );
}
